import React, { useEffect, useState, FormEvent } from 'react';
import Button from '../common/Button';
import Input from '../common/Input';
import Toast from '../common/Toast';
import { apiService } from '../../services/api';
import { authUtils } from '../../services/auth';
import { useAuth } from '../../hooks/useAuth';

import './RegisterModal.css'; // Reuse modal styles

interface UpdateUserInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface FormData {
  nickname: string;
  firstName: string;
  lastName: string;
}

interface FormErrors {
  nickname?: string;
  firstName?: string;
  lastName?: string;
  general?: string;
}

const UpdateUserInfoModal: React.FC<UpdateUserInfoModalProps> = ({ isOpen, onClose }) => {
  const { user, refreshUser } = useAuth();
  const currentNickname = (user?.nickname || user?.profile?.nickname || user?.username || '').trim();

  const [formData, setFormData] = useState<FormData>({
    nickname: '',
    firstName: '',
    lastName: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingNickname, setIsCheckingNickname] = useState(false);
  const [nicknameHelper, setNicknameHelper] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showToast, setShowToast] = useState(false);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setFormData({
        nickname: currentNickname,
        firstName: user?.profile?.firstName || '',
        lastName: user?.profile?.lastName || '',
      });
      setErrors({});
      setNicknameHelper('');
      setSuccessMessage('');
      setShowToast(false);
    }
  }, [isOpen, currentNickname, user?.profile?.firstName, user?.profile?.lastName]);

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
    if (errors.general) {
      setErrors((prev) => ({ ...prev, general: undefined }));
    }
    if (successMessage) {
      setSuccessMessage('');
    }
    if (name === 'nickname') {
      setNicknameHelper('');
    }
  };

  const validateNickname = (nickname: string): string | undefined => {
    if (!nickname) return 'Field Required';
    if (nickname.length < 3) return 'At least 3 characters';
    if (nickname.length > 40) return 'At most 40 characters';
    if (!/^[A-Za-z0-9_.\- ]+$/.test(nickname)) {
      return 'Only letters, numbers, spaces, underscores, hyphens, and periods';
    }
    return undefined;
  };

  const checkNicknameAvailability = async (nickname: string): Promise<boolean> => {
    const trimmed = nickname.trim();
    const validationError = validateNickname(trimmed);
    if (validationError) {
      setErrors((prev) => ({ ...prev, nickname: validationError }));
      setNicknameHelper('');
      return false;
    }

    if (trimmed.toLowerCase() === currentNickname.toLowerCase()) {
      setErrors((prev) => ({ ...prev, nickname: undefined }));
      setNicknameHelper('Current nickname');
      return true;
    }

    setIsCheckingNickname(true);
    try {
      const token = authUtils.getStoredToken();
      const response = await apiService.get('/users/check_nickname', {
        params: { nickname: trimmed },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response?.data?.available) {
        setErrors((prev) => ({ ...prev, nickname: undefined }));
        setNicknameHelper('Nickname is available');
        return true;
      }

      setErrors((prev) => ({ ...prev, nickname: 'Nickname is already in use' }));
      setNicknameHelper('');
      return false;
    } catch (error: any) {
      const message = error?.message || error?.detail || 'Could not verify nickname availability';
      setErrors((prev) => ({ ...prev, nickname: message }));
      setNicknameHelper('');
      return false;
    } finally {
      setIsCheckingNickname(false);
    }
  };

  const handleNicknameBlur = async () => {
    if (!formData.nickname.trim()) return;
    await checkNicknameAvailability(formData.nickname);
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    const nicknameError = validateNickname(formData.nickname.trim());
    if (nicknameError) newErrors.nickname = nicknameError;
    if (!formData.firstName.trim()) newErrors.firstName = 'Field Required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Field Required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validateForm()) return;

    const isNicknameAvailable = await checkNicknameAvailability(formData.nickname);
    if (!isNicknameAvailable) return;

    setIsLoading(true);
    try {
      const token = authUtils.getStoredToken();
      await apiService.post(
        '/users/update_names',
        {
          nickname: formData.nickname.trim(),
          first_name: formData.firstName,
          last_name: formData.lastName,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      await refreshUser();
      setSuccessMessage('Account information updated.');
      setShowToast(true);

      setTimeout(() => setShowToast(false), 2500);
      setTimeout(() => onClose(), 800);
    } catch (error: any) {
      let message = 'Update failed. Please try again.';
      if (error?.message) message = error.message;
      if (error?.detail) message = error.detail;

      setErrors((prev) => ({ ...prev, general: message }));
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <>
      {isOpen && (
        <div className="register-modal-backdrop" onClick={handleBackdropClick}>
          <div className="register-modal" role="dialog" aria-labelledby="update-user-info-modal-title" aria-modal="true">
            <div className="register-modal__header">
              <h2 id="update-user-info-modal-title" className="register-modal__title">Update User Information</h2>
              <button
                type="button"
                className="register-modal__close"
                onClick={onClose}
                aria-label="Close update user information modal"
                disabled={isLoading}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <form className="register-modal__form" onSubmit={handleSubmit} noValidate>
              {errors.general && (
                <div className="register-modal__error" role="alert">
                  <svg className="register-modal__error-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="15" y1="9" x2="9" y2="15" />
                    <line x1="9" y1="9" x2="15" y2="15" />
                  </svg>
                  {typeof errors.general === 'string' ? errors.general : JSON.stringify(errors.general)}
                </div>
              )}

              <div className="register-modal__fields">
                <div className="register-modal__field">
                  <Input
                    type="text"
                    name="nickname"
                    label="Nickname"
                    placeholder="Nickname"
                    value={formData.nickname}
                    onChange={handleInputChange}
                    onBlur={handleNicknameBlur}
                    error={errors.nickname}
                    helperText={isCheckingNickname ? 'Checking availability...' : nicknameHelper}
                    disabled={isLoading}
                    autoComplete="nickname"
                    required
                  />
                </div>
                <div className="register-modal__field">
                  <Input
                    type="text"
                    name="firstName"
                    label="First Name"
                    placeholder="First name"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    error={errors.firstName}
                    disabled={isLoading}
                    autoComplete="given-name"
                    required
                  />
                </div>
                <div className="register-modal__field">
                  <Input
                    type="text"
                    name="lastName"
                    label="Last Name"
                    placeholder="Last name"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    error={errors.lastName}
                    disabled={isLoading}
                    autoComplete="family-name"
                    required
                  />
                </div>
              </div>

              <div className="register-modal__actions">
                <Button type="button" variant="secondary" onClick={onClose} disabled={isLoading}>
                  Cancel
                </Button>
                <Button type="submit" variant="primary" loading={isLoading} disabled={isLoading}>
                  {isLoading ? 'Saving...' : 'Save'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showToast && (
        <Toast message={successMessage} duration={2500} onClose={() => setShowToast(false)} />
      )}
    </>
  );
};

export default UpdateUserInfoModal;
